// piano-frontend/src/pages/SettingsPage.js 수정

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  FormControl,
  Button,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Notifications,
  School,
  MusicNote,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { useDarkMode } from '../App';
import api from '../api/axios';
import PushNotificationManager from '../components/PushNotificationManager';

function SettingsPage({ user }) {
  const { darkMode } = useDarkMode(); // 남은 카드들의 배경색 분기에만 사용
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [settings, setSettings] = useState({
    notification_lesson_reminder: true,
    notification_lesson_approval: true,
    notification_practice_reminder: true,
    notification_admin_requests: true,
  });

  // 평일 연습실 시간 설정 (별도 관리)
  const [practiceBeforeLesson, setPracticeBeforeLesson] = useState(false);
  const [originalPracticeBeforeLesson, setOriginalPracticeBeforeLesson] = useState(false);
  const [savingPractice, setSavingPractice] = useState(false);
  const [practiceSuccess, setPracticeSuccess] = useState(false);

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/users/notification-settings');
      setSettings({
        notification_lesson_reminder: res.data.notification_lesson_reminder,
        notification_lesson_approval: res.data.notification_lesson_approval,
        notification_practice_reminder: res.data.notification_practice_reminder,
        notification_admin_requests: res.data.notification_admin_requests,
      });
      // 평일 연습실 시간 설정 별도 관리
      const practiceValue = res.data.practice_before_lesson || false;
      setPracticeBeforeLesson(practiceValue);
      setOriginalPracticeBeforeLesson(practiceValue);
    } catch (err) {
      console.error('알림 설정 조회 실패:', err);
      setError('알림 설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (key, value) => {
    try {
      setSaving(true);
      setError(null);

      const newSettings = { ...settings, [key]: value };
      setSettings(newSettings);

      await api.put('/users/notification-settings', { [key]: value });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('알림 설정 저장 실패:', err);
      setError('설정 저장에 실패했습니다.');
      fetchNotificationSettings();
    } finally {
      setSaving(false);
    }
  };

  // 평일 연습실 시간 저장 (별도 버튼)
  const handleSavePracticeTime = async () => {
    try {
      setSavingPractice(true);
      setError(null);

      await api.put('/users/notification-settings', { practice_before_lesson: practiceBeforeLesson });

      setOriginalPracticeBeforeLesson(practiceBeforeLesson);
      setPracticeSuccess(true);
      setTimeout(() => setPracticeSuccess(false), 2000);
    } catch (err) {
      console.error('평일 연습실 시간 저장 실패:', err);
      setError('설정 저장에 실패했습니다.');
      setPracticeBeforeLesson(originalPracticeBeforeLesson);
    } finally {
      setSavingPractice(false);
    }
  };

  const practiceTimeChanged = practiceBeforeLesson !== originalPracticeBeforeLesson;

  const handlePushStatusChange = (enabled) => {
    setIsPushEnabled(enabled);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2, pb: 2, px: 2, maxWidth: 600, mx: 'auto' }}>
      {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목 숨김 */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          설정
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          설정이 저장되었습니다.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 화면 설정(다크 모드)은 상단 헤더 토글로 이동 */}

      {/* 푸시 알림 설정 */}
      <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Notifications sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            푸시 알림
          </Typography>
        </Box>

        <PushNotificationManager 
          user={user} 
          onStatusChange={handlePushStatusChange}
        />

        {isPushEnabled && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 2, 
                color: 'text.secondary',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
            >
              알림 받을 항목 선택
            </Typography>

            {user?.role === 'MEMBER' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* 당일 레슨 리마인더 */}
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1.5, 
                    bgcolor: settings.notification_lesson_reminder ? (darkMode ? 'rgba(144, 202, 249, 0.15)' : '#e3f2fd') : (darkMode ? 'grey.800' : '#f5f5f5'),
                    transition: 'all 0.2s',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notification_lesson_reminder}
                        onChange={(e) => handleSettingChange('notification_lesson_reminder', e.target.checked)}
                        disabled={saving}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <School sx={{ fontSize: 20, color: settings.notification_lesson_reminder ? 'primary.main' : 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            당일 레슨 알림
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            레슨 당일 리마인더 알림
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>

                {/* 레슨 예약 승인/반려 */}
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1.5, 
                    bgcolor: settings.notification_lesson_approval ? (darkMode ? 'rgba(144, 202, 249, 0.15)' : '#e3f2fd') : (darkMode ? 'grey.800' : '#f5f5f5'),
                    transition: 'all 0.2s',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notification_lesson_approval}
                        onChange={(e) => handleSettingChange('notification_lesson_approval', e.target.checked)}
                        disabled={saving}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <CheckCircle sx={{ fontSize: 20, color: settings.notification_lesson_approval ? 'success.main' : 'text.secondary' }} />
                          <Cancel sx={{ fontSize: 20, color: settings.notification_lesson_approval ? 'error.main' : 'text.secondary' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            레슨 예약 승인/반려 알림
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            레슨 신청 결과 알림
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>

                {/* 당일 연습실 예약 리마인더 */}
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1.5, 
                    bgcolor: settings.notification_practice_reminder ? (darkMode ? 'rgba(144, 202, 249, 0.15)' : '#e3f2fd') : (darkMode ? 'grey.800' : '#f5f5f5'),
                    transition: 'all 0.2s',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notification_practice_reminder}
                        onChange={(e) => handleSettingChange('notification_practice_reminder', e.target.checked)}
                        disabled={saving}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MusicNote sx={{ fontSize: 20, color: settings.notification_practice_reminder ? 'primary.main' : 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            당일 연습실 예약 알림
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            연습실 예약 당일 리마인더 알림
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 1.5, 
                  bgcolor: settings.notification_admin_requests ? (darkMode ? 'rgba(144, 202, 249, 0.15)' : '#e3f2fd') : (darkMode ? 'grey.800' : '#f5f5f5'),
                  transition: 'all 0.2s',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notification_admin_requests}
                      onChange={(e) => handleSettingChange('notification_admin_requests', e.target.checked)}
                      disabled={saving}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Notifications sx={{ fontSize: 20, color: settings.notification_admin_requests ? 'primary.main' : 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          레슨 예약/취소 요청 알림
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          회원의 레슨 예약 및 취소 신청 알림
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 평일 연습실 시간 설정 (MEMBER만) */}
      {user?.role === 'MEMBER' && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Schedule sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              평일 연습실 시간 설정
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            평일 레슨 시 연습실 사용 시간을 선택하세요.
          </Typography>

          {practiceSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              평일 연습실 시간 설정이 저장되었습니다.
            </Alert>
          )}

          <FormControl component="fieldset">
            <RadioGroup
              value={practiceBeforeLesson ? 'before' : 'after'}
              onChange={(e) => setPracticeBeforeLesson(e.target.value === 'before')}
            >
              <FormControlLabel
                value="after"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      레슨 후 30분 연습 (기본)
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      예: 7시 레슨 → 7시 30분~8시 연습
                    </Typography>
                  </Box>
                }
                disabled={savingPractice}
              />
              <FormControlLabel
                value="before"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      레슨 전 30분 연습
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      예: 7시 레슨 → 6시 30분~7시 연습
                    </Typography>
                  </Box>
                }
                disabled={savingPractice}
              />
            </RadioGroup>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            onClick={handleSavePracticeTime}
            disabled={!practiceTimeChanged || savingPractice}
            sx={{ mt: 2 }}
          >
            {savingPractice ? '저장 중...' : practiceTimeChanged ? '저장' : '변경 사항 없음'}
          </Button>

          <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
            평일 레슨 시 Lesson 3-6 연습실에서 30분 연습이 자동 예약됩니다.
          </Alert>
        </Paper>
      )}

      {/* 안내 문구 */}
      <Paper sx={{ p: 2, bgcolor: darkMode ? 'grey.800' : '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
          💡 알림 팁
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
          • 푸시 알림을 끄면 모든 세부 알림이 비활성화됩니다.
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
          • 브라우저 설정에서 알림 권한을 허용해야 합니다.
        </Typography>
        {user?.role === 'MEMBER' && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
              • 레슨 알림은 평일 오후 2시, 토요일 오전 10시에 발송됩니다.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
              • 연습실 알림은 평일 오후 2시, 토요일 오전 10시에 발송됩니다.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
              • 평일 연습실 시간 설정 변경 시 매일 자정에 변경이 적용됩니다.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default SettingsPage;